// Users (with their permission groups), settings, audit logs.
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Save } from 'lucide-react';
import { api, errorText } from '@/lib/api';
import { PageHeader, DataTable, Pager, ErrorBox, Field } from '@/components/common';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/input';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { StatusBadge } from '@/components/ui/badge';
import { useRefOptions, rowLabel } from '@/hooks/meta';
import { toast } from '@/store/toast';
import { useAuth, useCan, useModule } from '@/store/auth';
import { fmtTime, enumLabel } from '@/lib/utils';
import { ImportButton } from '@/components/ImportDialog';
import { t, LANGUAGES } from '@/i18n';

export function UsersPage() {
  const me = useAuth((s) => s.user);
  const users = useQuery({ queryKey: ['users'], queryFn: () => api<{ rows: any[] }>('/users') });
  const groups = useGroups();
  const [canAdd, canEdit, canDelete] = [useCan('users.add'), useCan('users.edit'), useCan('users.delete')];
  const pbx = useModule('PBX');
  const cc = useModule('CALLCENTER');
  const groupNames = (ids: number[] | undefined) => (ids ?? []).map((id) => groups.data?.find((g) => g.id === id)?.name ?? `#${id}`).join(', ');
  const [edit, setEdit] = React.useState<any | null | undefined>(undefined);
  async function remove(u: any) {
    if (!confirm(t('Delete user {{name}}?', { name: u.username }))) return;
    try { await api(`/users/${u.id}`, { method: 'DELETE' }); toast.ok(t('Deleted')); users.refetch(); } catch (e) { toast.error(errorText(e)); }
  }
  return (
    <div>
      <PageHeader title={t('Users')} description={t('Who can sign in to the portals. Agent users are tied to one agent. Supervisors are agents or phone users in the permission group Supervisor.')}>
        <ImportButton kind="users" title={t('Import users')} right="users.add" refresh={['users', 'agents', 'extensions']} />
        {canAdd && <Button onClick={() => setEdit(null)}><Plus /> {t('Add')}</Button>}
      </PageHeader>
      <ErrorBox error={users.error} />
      <DataTable rows={users.data?.rows} columns={[
        { key: 'username', label: t('Username') }, { key: 'name', label: t('Name') }, { key: 'email', label: t('Email') },
        { key: 'role', label: t('Role'), render: (u) => <StatusBadge value={u.role} /> },
        { key: 'group_ids', label: t('Permission groups'), render: (u) => u.group_ids?.length ? groupNames(u.group_ids) : u.role === 'SUPERVISOR' ? t('Supervisor (built in)') : '' },
        ...(cc ? [{ key: 'agent_code', label: t('Agent') }] : []),
        ...(pbx ? [{ key: 'extension_number', label: t('Extension') }] : []),
        { key: 'status', label: t('Status'), render: (u) => <StatusBadge value={u.status} /> }, { key: 'last_login_at', label: t('Last login'), render: (u) => fmtTime(u.last_login_at) }]}
        actions={(u) => (<>
          {canEdit && <Button size="icon" variant="ghost" aria-label={t('Edit')} onClick={() => setEdit(u)}><Pencil /></Button>}
          {canDelete && u.id !== me?.id && <Button size="icon" variant="ghost" aria-label={t('Delete')} onClick={() => remove(u)}><Trash2 className="text-destructive" /></Button>}
        </>)} />
      <Dialog open={edit !== undefined} onOpenChange={(o) => !o && setEdit(undefined)}>
        {edit !== undefined && <DialogContent title={edit ? t('Edit {{name}}', { name: edit.username }) : t('New user')}>
          <UserForm user={edit} onDone={() => { setEdit(undefined); users.refetch(); }} />
        </DialogContent>}
      </Dialog>
    </div>
  );
}

export interface PermissionGroup { id: number; name: string; description: string | null; users: number; permissions: string[]; builtin?: boolean }
export function useGroups() {
  return useQuery({ queryKey: ['permission-groups'], queryFn: async () => (await api<{ rows: PermissionGroup[] }>('/permission-groups')).rows });
}

interface NewAgent { id: number; agent_code: string; sip_username: string; sip_domain: string; pin: string }
interface NewExtension { id: number; number: string; sip_username: string | null; sip_password: string | null; sip_domain: string; voicemail_pin: string | null }

function UserForm({ user, onDone }: { user: any | null; onDone: () => void }) {
  const pbx = useModule('PBX');
  const cc = useModule('CALLCENTER');
  const agents = useRefOptions('agents', cc);
  const exts = useRefOptions('extensions', pbx);
  const isPlatform = useAuth((s) => s.user?.role === 'PLATFORM');
  // without every right (a supervisor given Users by a group) only supervisors and agents can be managed
  const everything = useAuth((s) => !!s.permissions?.includes('*') || (!s.permissions && s.user?.role !== 'SUPERVISOR'));
  const groups = useGroups();
  const [v, setV] = React.useState<Record<string, any>>(() => user
    ? { name: user.name, email: user.email ?? '', role: user.role, agent_id: user.agent_id ?? '', status: user.status, group_ids: user.group_ids ?? [], language: user.language ?? '',
        extension_id: user.extension_id ? String(user.extension_id) : '' }
    : { username: '', password: '', name: '', email: '', role: cc ? 'AGENT' : 'USER', agent_id: '', status: 'ACTIVE', group_ids: [], language: '', extension_id: 'NEW' });
  const toggleGroup = (id: number) => setV((s) => ({ ...s, group_ids: s.group_ids.includes(id) ? s.group_ids.filter((x: number) => x !== id) : [...s.group_ids, id] }));
  const [error, setError] = React.useState<unknown>(null);
  // an AGENT user saved without an agent got a new one: shown once (the PIN is not shown again)
  const [created, setCreated] = React.useState<NewAgent | null>(null);
  // a user saved without an extension in a PBX tenant got a new one: its phone account and voicemail PIN, shown once
  const [newExt, setNewExt] = React.useState<NewExtension | null>(null);
  const grouped = v.role !== 'ADMIN' && v.role !== 'PLATFORM';
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setV((s) => ({ ...s, [k]: e.target.value }));
  async function submit(e: React.FormEvent) {
    e.preventDefault(); setError(null);
    const body: Record<string, unknown> = { ...v, agent_id: v.agent_id ? Number(v.agent_id) : null, email: v.email || null, language: v.language || null,
                                            group_ids: grouped ? v.group_ids : [] };
    if (user && grouped && [...(user.group_ids ?? [])].sort().join() === [...v.group_ids].sort().join()) delete body.group_ids;
    // extension (PBX): NEW = make one, NONE = none, else the id picked
    delete body.extension_id;
    if (pbx && v.role !== 'PLATFORM') {
      if (!user) { if (v.extension_id === 'NONE') body.no_extension = true; else if (v.extension_id !== 'NEW') body.extension_id = Number(v.extension_id); }
      else if (String(user.extension_id ?? '') !== v.extension_id) body.extension_id = v.extension_id && v.extension_id !== 'NONE' ? Number(v.extension_id) : null;
    }
    if (user && !v.password) delete body.password;
    try {
      type Saved = { agent?: NewAgent | null; extension?: NewExtension | null };
      const r = user ? await api<Saved>(`/users/${user.id}`, { method: 'PATCH', body }) : await api<Saved>('/users', { body });
      toast.ok(t('Saved'));
      if (r.agent || r.extension) { setCreated(r.agent ?? null); setNewExt(r.extension ?? null); } else onDone();
    } catch (err) { setError(err); }
  }
  if (created || newExt) return (
    <div className="grid gap-3 text-sm">
      {created && <div className="grid gap-1" data-testid="agent-created">
        <p>{t('A new agent was created for this user: agent {{code}}, SIP user {{sip}}.', { code: created.agent_code, sip: `${created.sip_username}@${created.sip_domain}` })}</p>
        <p>{t('Desk phone PIN: {{pin}}. It is shown only now; the portal signs the agent in without it.', { pin: created.pin })}</p>
      </div>}
      {newExt && <div className="grid gap-1" data-testid="extension-created">
        <p>{t('A new extension was created for this user: {{number}}.', { number: newExt.number })}</p>
        {newExt.sip_username && <p>{t('Phone account: SIP user {{sip}}, password {{password}}.', { sip: `${newExt.sip_username}@${newExt.sip_domain}`, password: newExt.sip_password ?? '' })}</p>}
        {newExt.voicemail_pin && <p>{t('Voicemail PIN: {{pin}}.', { pin: newExt.voicemail_pin })}</p>}
        <p className="text-xs text-muted-foreground">{t('Shown only now. The phone can also be set up from the Phones page.')}</p>
      </div>}
      <div className="flex justify-end"><Button onClick={onDone}>{t('Done')}</Button></div>
    </div>
  );
  return (
    <form onSubmit={submit} className="grid gap-3">
      {!user && <Field help="users.username" label={t('Username')}><Input value={v.username} onChange={set('username')} required pattern="[A-Za-z0-9._\-]{3,64}" /></Field>}
      <Field help="users.password" label={user ? t('New password') : t('Password')} hint={t('At least 8 characters.')}><Input type="password" autoComplete="new-password" value={v.password ?? ''} onChange={set('password')} required={!user} /></Field>
      <Field help="users.name" label={t('Name')}><Input value={v.name} onChange={set('name')} required /></Field>
      <Field help="users.email" label={t('Email')}><Input type="email" value={v.email} onChange={set('email')} /></Field>
      <Field help="users.role" label={t('Role')}><Select name="role" value={v.role} onChange={set('role')}>{isPlatform && <option value="PLATFORM">{enumLabel('PLATFORM')}</option>}{everything && <option value="ADMIN">{enumLabel('ADMIN')}</option>}{user?.role === 'SUPERVISOR' && <option value="SUPERVISOR">{t('Supervisor (old user type)')}</option>}{cc && <option value="AGENT">{enumLabel('AGENT')}</option>}{pbx && <option value="USER">{enumLabel('USER')}</option>}</Select></Field>
      {v.role === 'USER' && <p className="text-xs text-muted-foreground sm:col-span-2">{t('A phone user signs in to their own extension page. Permission groups add admin pages.')}</p>}
      {pbx && v.role !== 'PLATFORM' && (
        <Field help="users.extension" label={t('Extension')} hint={v.extension_id === 'NEW' ? t('A new extension is created with the next free number, a phone account and a voicemail box.') : undefined}>
          <Select name="extension_id" value={v.extension_id} onChange={set('extension_id')}>
            {!user && <option value="NEW">{t('(create a new extension)')}</option>}
            {(user ? v.role !== 'USER' || !user.extension_id : v.role !== 'USER') && <option value={user ? '' : 'NONE'}>{t('(no extension)')}</option>}
            {(exts.data ?? []).filter((e) => !e.user_id || (user && Number(e.user_id) === Number(user.id))).map((e) => <option key={e.id} value={String(e.id)}>{rowLabel(e)}</option>)}
          </Select>
        </Field>
      )}
      {grouped && (
        <div className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium">{t('Permission groups')}</span>
          <div className="grid gap-1 rounded-md border p-2" data-testid="user-groups">
            {(groups.data ?? []).filter((g) => !g.builtin).map((g) => (
              <label key={g.id} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={v.group_ids.includes(g.id)} onChange={() => toggleGroup(g.id)} aria-label={t('Group {{name}}', { name: g.name })} /> {g.name}
              </label>
            ))}
            {!(groups.data ?? []).some((g) => !g.builtin) && <span className="text-sm text-muted-foreground">{t('No groups yet (Admin → Permission groups).')}</span>}
          </div>
          <span className="text-xs text-muted-foreground">{v.role === 'SUPERVISOR' ? t('None ticked: the built-in Supervisor rights. Ticked: exactly what the groups allow.')
            : t('Rights added to the role. A supervisor is an agent or phone user in the group Supervisor.')}</span>
        </div>
      )}
      {cc && v.role !== 'USER' && <Field help="users.agent" label={t('Agent')} hint={v.role === 'AGENT' && !v.agent_id ? t('None picked: a new agent is created with the next free number and the company SIP domain.') : undefined}>
        <Select name="agent_id" value={v.agent_id} onChange={set('agent_id')}><option value="">{v.role === 'AGENT' ? t('(create a new agent)') : t('(none)')}</option>
        {(agents.data ?? []).map((a) => <option key={a.id} value={a.id}>{rowLabel(a)}</option>)}</Select></Field>}
      <Field help="users.language" label={t('Portal language')} hint={t('What this user sees in the portal. Callers hear the voice language of the number they dialled.')}>
        <Select name="language" value={v.language} onChange={set('language')}><option value="">{t('Company default')}</option>
          {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}</Select></Field>
      <Field help="users.status" label={t('Status')}><Select value={v.status} onChange={set('status')}><option value="ACTIVE">{enumLabel('ACTIVE')}</option><option value="DISABLED">{enumLabel('DISABLED')}</option></Select></Field>
      <ErrorBox error={error} />
      <div className="flex justify-end"><Button type="submit">{t('Save')}</Button></div>
    </form>
  );
}

export function SettingsPage() {
  const s = useQuery({ queryKey: ['settings'], queryFn: () => api<{ rows: any[] }>('/settings') });
  const [draft, setDraft] = React.useState<Record<string, string>>({});
  async function save(key: string) {
    try { await api(`/settings/${key}`, { method: 'PUT', body: { value: draft[key] } }); toast.ok(t('{{key}} saved', { key })); setDraft((d) => { const n = { ...d }; delete n[key]; return n; }); s.refetch(); }
    catch (e) { toast.error(errorText(e)); }
  }
  return (
    <div>
      <PageHeader title={t('Settings')} description={t('Engine settings. A company value overrides the system default; workers pick changes up on their next cycle.')} />
      <ErrorBox error={s.error} />
      <DataTable rowKey={(r) => r.setting_key} rows={s.data?.rows} columns={[
        { key: 'setting_key', label: t('Setting'), render: (r) => <code className="text-xs">{r.setting_key}</code> },
        { key: 'description', label: t('Description'), className: 'whitespace-normal max-w-md' },
        { key: 'system_value', label: t('Default') },
        { key: 'value', label: t('Value'), render: (r) => <Input className="h-8 w-48" value={draft[r.setting_key] ?? r.value ?? ''} onChange={(e) => setDraft((d) => ({ ...d, [r.setting_key]: e.target.value }))} /> }]}
        actions={(r) => draft[r.setting_key] !== undefined ? <Button size="sm" onClick={() => save(r.setting_key)}><Save /> {t('Save')}</Button> : null} />
    </div>
  );
}

export function AuditLogsPage() {
  const [action, setAction] = React.useState('');
  const [offset, setOffset] = React.useState(0);
  const q = useQuery({ queryKey: ['audit', action, offset], queryFn: () => api<{ rows: any[] }>('/audit-logs', { query: { action, limit: 50, offset, from: '2000-01-01' } }) });
  return (
    <div>
      <PageHeader title={t('Audit Logs')} description={t("Who changed what: portal users, agents, the engine's workers.")} />
      <div className="mb-3"><Input className="w-64" placeholder={t('Action starts with… (e.g. users.)')} value={action} onChange={(e) => { setAction(e.target.value); setOffset(0); }} /></div>
      <ErrorBox error={q.error} />
      <DataTable rows={q.data?.rows} columns={[
        { key: 'created_at', label: t('When'), render: (r) => fmtTime(r.created_at) }, { key: 'actor_type', label: t('Actor'), render: (r) => <StatusBadge value={r.actor_type} /> },
        { key: 'actor_id', label: t('Who') }, { key: 'action', label: t('Action') }, { key: 'entity_type', label: t('Entity') }, { key: 'entity_id', label: t('Id') },
        { key: 'details', label: t('Details'),
 className: 'whitespace-normal max-w-md', render: (r) => <code className="text-xs break-all">{r.details ? JSON.stringify(r.details) : ''}</code> }]} />
      <Pager offset={offset} limit={50} total={q.data && q.data.rows.length < 50 ? offset + q.data.rows.length : undefined} onChange={setOffset} />
    </div>
  );
}
